import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Camera,
    Check,
    CheckCircle2,
    LifeBuoy,
    Mic,
    Paperclip,
    Send,
    ShieldAlert,
    Video,
    WifiOff,
    X,
} from 'lucide-react'

import { useAuth } from '../../contexts/AuthContext'
import {
    completeAttachment,
    createTicket,
    sendChat,
    streamUpload,
    uploadAttachmentTyped,
    uploadScreenshot,
    uploadToStream,
} from '../../api/support'
import { getBrowserMetaSnapshot, getRecentErrors } from '../../utils/supportContext'
import { captureScreenshot } from './ScreenshotCapture'
import BlurBrush from './BlurBrush'
import VideoRecorder from './VideoRecorder'
import AudioRecorder from './AudioRecorder'
import SupportHowItWorks from './SupportHowItWorks'
import SupportPrivacyHint from './SupportPrivacyHint'
import SupportMediaToolbar from './SupportMediaToolbar'
import {
    Composer,
    Conversation,
    ConversationHeader,
    EmptyState,
    MessageStream,
} from '../conversation'

const HIDDEN_ROUTE_PREFIXES = ['/login', '/test/', '/sso/', '/offer-signing/']

// Chat-first quick prompts (map to how_to / bug / access intents server-side).
const CHAT_SUGGESTIONS = [
    'Как выгрузить данные?',
    'Страница не открывается',
    'Нет доступа к разделу',
    'Кажется, это ошибка',
]
const STATES = {
    IDLE: 'idle',
    CONSENT: 'consent',
    CAPTURING: 'capturing',
    PREVIEWING: 'previewing',
    RECORDING_VIDEO: 'recording_video',
    RECORDING_AUDIO: 'recording_audio',
    PREVIEWING_VIDEO: 'previewing_video',
    PREVIEWING_AUDIO: 'previewing_audio',
    SUBMITTING: 'submitting',
    DONE: 'done',
    ERROR: 'error',
}

export default function SupportWidget() {
    const { user } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    const widgetRef = useRef(null)

    const [open, setOpen] = useState(false)
    const [state, setState] = useState(STATES.IDLE)
    const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
    const [outboxDrained, setOutboxDrained] = useState(0)
    const [summary, setSummary] = useState('')
    const [description, setDescription] = useState('')
    const [withScreenshot, setWithScreenshot] = useState(false)
    const [screenshotBlob, setScreenshotBlob] = useState(null)
    const [screenshotDataUrl, setScreenshotDataUrl] = useState(null)
    const [maskedCount, setMaskedCount] = useState(0)
    const [videoBlob, setVideoBlob] = useState(null)
    const [videoMime, setVideoMime] = useState(null)
    const [audioBlob, setAudioBlob] = useState(null)
    const [audioMime, setAudioMime] = useState(null)
    const [error, setError] = useState('')
    const [createdTicketId, setCreatedTicketId] = useState(null)

    // ── Conversational mode (C-3): chat is the default; the form is the
    //    fallback for attachments/offline. ──
    const [mode, setMode] = useState('chat') // 'chat' | 'form'
    const [messages, setMessages] = useState([]) // { id, role, content, sources? }
    const [chatInput, setChatInput] = useState('')
    const [chatSending, setChatSending] = useState(false)
    const [chatTicketId, setChatTicketId] = useState(null)
    // C-5: media staged for the next chat message (agent analyses it multimodally).
    const [chatMedia, setChatMedia] = useState([]) // { kind, mime, dataBase64, name, size }
    // Large-video upload straight to Cloudflare Stream (browser→Stream direct) — progress chip.
    const [uploadingVideo, setUploadingVideo] = useState(null) // { name, pct } | null
    // U-1/U-2/U-3 one-window: privacy popover, drag overlay, inline recorder.
    const [showCollect, setShowCollect] = useState(false) // «Что мы собираем» popover
    const [dragActive, setDragActive] = useState(false) // drop overlay visible
    const dragDepth = useRef(0) // dragenter/leave counter (anti-flicker)
    const [chatRecording, setChatRecording] = useState(null) // 'video' | 'audio' | null
    const [pasteNote, setPasteNote] = useState('') // just-in-time note under chips

    const isHiddenRoute = useMemo(
        () => HIDDEN_ROUTE_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p)),
        [location.pathname],
    )

    const _reset = useCallback(() => {
        setSummary('')
        setDescription('')
        setWithScreenshot(false)
        setScreenshotBlob(null)
        setScreenshotDataUrl(null)
        setMaskedCount(0)
        setVideoBlob(null)
        setVideoMime(null)
        setAudioBlob(null)
        setAudioMime(null)
        setError('')
        setCreatedTicketId(null)
        setState(STATES.IDLE)
        setMode('chat')
        setMessages([])
        setChatInput('')
        setChatSending(false)
        setChatTicketId(null)
        setChatMedia([])
        setUploadingVideo(null)
        setShowCollect(false)
        setDragActive(false)
        dragDepth.current = 0
        setChatRecording(null)
        setPasteNote('')
    }, [])

    // Map a MIME type to the SupportGate attachment kind (whitelist-aligned).
    const _kindForMime = (mime) => {
        if (/^image\/(png|jpeg|webp)$/i.test(mime)) return 'screenshot'
        if (/^video\//i.test(mime)) return 'video'
        if (/^audio\//i.test(mime)) return 'audio'
        return 'file'
    }

    const _CHAT_MEDIA_TOTAL_CAP = 17 * 1024 * 1024 // keep under the 18 MB backend cap
    const _STREAM_MAX_VIDEO = 500 * 1024 * 1024 // big video → Cloudflare Stream direct upload

    const _stageMedia = useCallback((item) => {
        setChatMedia((prev) => {
            const used = prev.reduce((s, m) => s + (m.size || 0), 0)
            if (used + (item.size || 0) > _CHAT_MEDIA_TOTAL_CAP) {
                setError('Слишком большой файл для чата (лимит ~17 МБ). Используйте форму с вложениями.')
                return prev
            }
            return [...prev, item]
        })
    }, [])

    // Read a File/Blob → { kind, mime, dataBase64, name, size }.
    const _blobToMedia = (blob, name) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onerror = () => reject(reader.error || new Error('read failed'))
            reader.onload = () => {
                const url = String(reader.result || '')
                const base64 = url.includes(',') ? url.slice(url.indexOf(',') + 1) : ''
                const mime = blob.type || 'application/octet-stream'
                resolve({ kind: _kindForMime(mime), mime, dataBase64: base64, name: name || 'file', size: blob.size || 0 })
            }
            reader.readAsDataURL(blob)
        })

    // Pasted/dropped/picked images are NOT auto-masked (not our DOM) — be honest.
    const _NOT_MASKED_NOTE =
        'Вставленные и перетащенные картинки не маскируются автоматически — удалите лишнее перед отправкой.'

    // Ensure a ticket exists for out-of-band uploads (Stream). Reuses the chat
    // ticket if present, otherwise creates one. Offline → surfaces as an error.
    const _ensureChatTicket = useCallback(async () => {
        if (chatTicketId) return chatTicketId
        const res = await createTicket({
            summary: 'Вложение: видео',
            route: location.pathname,
            browserMeta: getBrowserMetaSnapshot(),
        })
        if (res?._queued || !res?.id) {
            throw new Error('Нет связи — не удалось создать обращение для загрузки.')
        }
        setChatTicketId(res.id)
        return res.id
    }, [chatTicketId, location.pathname])

    // Large video → upload straight to Cloudflare Stream (direct creator upload,
    // resumable tus), with a progress chip. Degrades gracefully when Stream is
    // not configured (501).
    const _uploadLargeVideo = useCallback(async (file) => {
        setError('')
        const mime = file.type || 'video/mp4'
        const filename = file.name || 'video.mp4'
        const sizeBytes = file.size || 0
        let ticketId
        try {
            ticketId = await _ensureChatTicket()
        } catch (e) {
            setError(e?.message || 'Не удалось подготовить загрузку.')
            return
        }
        setUploadingVideo({ name: filename, pct: 0 })
        try {
            let minted
            try {
                minted = await streamUpload(ticketId, { filename, mime, sizeBytes, kind: 'video' })
            } catch (e) {
                if (e?.status === 501) {
                    // Stream not configured — fall back to the legacy multipart path if small enough.
                    if (sizeBytes <= 50 * 1024 * 1024) {
                        await uploadAttachmentTyped(ticketId, file, 'video', { filename, mime })
                        setUploadingVideo(null)
                        setMessages((prev) => [
                            ...prev,
                            { id: `s-${Date.now()}`, role: 'assistant', content: `Видео «${filename}» приложено к обращению.` },
                        ])
                        return
                    }
                    setUploadingVideo(null)
                    setError('Большие видео пока недоступны — пришлите видео ссылкой или сожмите его.')
                    return
                }
                throw e
            }
            await uploadToStream(minted.upload_url, file, {
                onProgress: (pct) => setUploadingVideo({ name: filename, pct }),
            })
            await completeAttachment(ticketId, {
                uid: minted.uid,
                kind: 'video',
                mime,
                sizeBytes,
                filename,
            })
            setUploadingVideo(null)
            setMessages((prev) => [
                ...prev,
                {
                    id: `s-${Date.now()}`,
                    role: 'assistant',
                    content: `Видео «${filename}» (${Math.round(sizeBytes / (1024 * 1024))} МБ) загружено и приложено к обращению.`,
                },
            ])
        } catch (e) {
            setUploadingVideo(null)
            setError(`Не удалось загрузить видео: ${e?.message || 'ошибка'}.`)
        }
    }, [_ensureChatTicket])

    const _ingestFiles = useCallback(async (files, { external = false } = {}) => {
        let sawImage = false
        for (const f of files) {
            if (!f) continue
            // Oversized file: route big video to Cloudflare Stream; guide the user otherwise.
            if ((f.size || 0) > _CHAT_MEDIA_TOTAL_CAP) {
                const kind = _kindForMime(f.type || '')
                if (kind === 'video' && (f.size || 0) <= _STREAM_MAX_VIDEO) {
                    await _uploadLargeVideo(f)
                } else if (kind === 'video') {
                    setError('Видео больше 500 МБ — сожмите его или пришлите ссылкой.')
                } else {
                    setError('Слишком большой файл для чата (лимит ~17 МБ). Крупное видео можно приложить как вложение.')
                }
                continue
            }
            if ((f.type || '').startsWith('image/')) sawImage = true
            try {
                _stageMedia(await _blobToMedia(f, f.name || 'file'))
            } catch {
                setError('Не удалось прочитать файл.')
            }
        }
        if (external && sawImage) setPasteNote(_NOT_MASKED_NOTE)
    }, [_stageMedia, _uploadLargeVideo])

    const _onPickFiles = useCallback(async (event) => {
        const files = Array.from(event?.target?.files || [])
        event.target.value = '' // allow re-picking the same file
        await _ingestFiles(files, { external: true })
    }, [_ingestFiles])

    const _captureScreenshotToChat = useCallback(async () => {
        setError('')
        try {
            const { blob, maskedSelectorCount } = await captureScreenshot({ hideElement: widgetRef.current })
            _stageMedia(await _blobToMedia(blob, 'screenshot.png'))
            setPasteNote(
                maskedSelectorCount > 0
                    ? `Авто-маска: закрыто элементов — ${maskedSelectorCount}. Проверьте превью перед отправкой.`
                    : 'Скриншот добавлен. Проверьте, что на нём нет лишних данных.',
            )
        } catch (e) {
            setError(`Не удалось сделать скриншот: ${e?.message || 'ошибка'}. Можно приложить файл.`)
        }
    }, [_stageMedia])

    // U-2: drag-drop onto the panel (overlay) + a record-to-chat helper for U-3.
    const _onDragEnter = useCallback((e) => {
        if (mode !== 'chat') return
        e.preventDefault()
        dragDepth.current += 1
        setDragActive(true)
    }, [mode])
    const _onDragOver = useCallback((e) => { if (mode === 'chat') e.preventDefault() }, [mode])
    const _onDragLeave = useCallback((e) => {
        if (mode !== 'chat') return
        e.preventDefault()
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragActive(false)
    }, [mode])
    const _onDrop = useCallback((e) => {
        if (mode !== 'chat') return
        e.preventDefault()
        dragDepth.current = 0
        setDragActive(false)
        const files = Array.from(e.dataTransfer?.files || [])
        if (files.length) _ingestFiles(files, { external: true })
    }, [mode, _ingestFiles])

    // U-3: a recorder (video/voice) finished → stage its blob like any attachment,
    // staying in the chat (no form-mode switch).
    const _onRecordingComplete = useCallback(async ({ blob, mime }) => {
        setChatRecording(null)
        try {
            const ext = (mime || '').includes('audio') ? 'voice.webm' : 'recording.webm'
            const named = new File([blob], ext, { type: mime || blob.type || 'application/octet-stream' })
            _stageMedia(await _blobToMedia(named, ext))
        } catch {
            setError('Не удалось приложить запись.')
        }
    }, [_stageMedia])

    // One conversational round-trip: append the user message (+ any staged
    // media), ask the agent, append its reply (+ citation as a source chip).
    // Network failure points the user at the form (which has the offline outbox).
    const _sendChat = useCallback(async (text) => {
        const trimmed = (text || '').trim()
        const media = chatMedia
        if ((!trimmed && media.length === 0) || chatSending) return
        // U-3: offline — keep the message + attachments staged (don't lose them),
        // show an inline banner instead of switching to the form.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setError('Нет связи — сообщение и вложения сохранены. Отправьте ещё раз, когда сеть появится.')
            return
        }
        const shownText = trimmed || (media.length ? '(вложение)' : '')
        setError('')
        setPasteNote('')
        setChatInput('')
        setChatMedia([])
        setMessages((prev) => [
            ...prev,
            {
                id: `u-${Date.now()}`,
                role: 'user',
                content: media.length ? `${shownText}  📎×${media.length}` : shownText,
            },
        ])
        setChatSending(true)
        try {
            const res = await sendChat({
                message: trimmed || 'Вложение во вложении — посмотрите, пожалуйста.',
                ticketId: chatTicketId,
                route: location.pathname,
                browserMeta: getBrowserMetaSnapshot(),
                media,
            })
            if (res?.ticket_id) setChatTicketId(res.ticket_id)
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}`,
                    role: 'assistant',
                    content: res?.reply_md || 'Готово.',
                    sources: res?.citation ? [res.citation] : null,
                },
            ])
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                    id: `e-${Date.now()}`,
                    role: 'assistant',
                    content: `Не удалось получить ответ (${e?.message || 'сеть'}). Попробуйте ещё раз или приложите скриншот.`,
                },
            ])
        } finally {
            setChatSending(false)
        }
    }, [chatSending, chatTicketId, location.pathname, chatMedia])

    // AutoErrorToast prefill handler — opens widget with [Авто] summary
    // populated from window.onerror message and the recent-errors breadcrumb.
    useEffect(() => {
        if (typeof window === 'undefined') return
        const onPrefill = (event) => {
            const detail = event?.detail || {}
            setOpen(true)
            // Auto-error prefill carries a stack trace — route it to the form.
            setMode('form')
            setSummary(detail.summary || '')
            const recent = getRecentErrors().slice(-5)
            if (recent.length) {
                const trace = recent.map((e) => `• ${e.at} — ${e.message}${e.stack ? `\n${e.stack.slice(0, 400)}` : ''}`).join('\n')
                setDescription((prev) => prev ? prev : `Последние ошибки:\n${trace}`)
            }
            setState(STATES.IDLE)
        }
        const onOpen = () => {
            setOpen(true)
        }
        const onOnline = () => setOnline(true)
        const onOffline = () => setOnline(false)
        const onDrained = (event) => {
            const sent = event?.detail?.sent || 0
            if (sent > 0) setOutboxDrained(sent)
        }
        window.addEventListener('onboardix:support:prefill', onPrefill)
        window.addEventListener('onboardix:support:open', onOpen)
        window.addEventListener('online', onOnline)
        window.addEventListener('offline', onOffline)
        window.addEventListener('onboardix:support:outbox:drained', onDrained)
        return () => {
            window.removeEventListener('onboardix:support:prefill', onPrefill)
            window.removeEventListener('onboardix:support:open', onOpen)
            window.removeEventListener('online', onOnline)
            window.removeEventListener('offline', onOffline)
            window.removeEventListener('onboardix:support:outbox:drained', onDrained)
        }
    }, [])

    // U-2: Ctrl+V image from clipboard → staged chip (Slack/Linear/GitHub model).
    // Window-level while the chat is open; text-only paste is left to the textarea.
    useEffect(() => {
        if (!open || mode !== 'chat') return
        const onPaste = (e) => {
            const items = Array.from(e.clipboardData?.items || [])
            const imageItems = items.filter((it) => it.kind === 'file' && (it.type || '').startsWith('image/'))
            if (!imageItems.length) return // ordinary text paste — don't intercept
            e.preventDefault()
            const files = imageItems.map((it) => it.getAsFile()).filter(Boolean)
            if (files.length) _ingestFiles(files, { external: true })
        }
        window.addEventListener('paste', onPaste)
        return () => window.removeEventListener('paste', onPaste)
    }, [open, mode, _ingestFiles])

    const _openPanel = () => {
        setOpen(true)
    }

    const _closePanel = () => {
        setOpen(false)
        // Reset after close-animation idle.
        setTimeout(_reset, 200)
    }

    const _capture = async () => {
        setError('')
        setState(STATES.CAPTURING)
        try {
            const { blob, dataUrl, maskedSelectorCount } = await captureScreenshot({
                hideElement: widgetRef.current,
            })
            setScreenshotBlob(blob)
            setScreenshotDataUrl(dataUrl)
            setMaskedCount(maskedSelectorCount)
            setState(STATES.PREVIEWING)
        } catch (e) {
            setError(`Не удалось сделать скриншот: ${e?.message || 'unknown'}. Прикрепите файл вручную.`)
            setWithScreenshot(false)
            setState(STATES.IDLE)
        }
    }

    const _submit = async (e) => {
        e?.preventDefault?.()
        if (!summary.trim()) {
            setError('Кратко опишите тему обращения.')
            return
        }
        setState(STATES.SUBMITTING)
        setError('')
        try {
            const ticket = await createTicket({
                summary: summary.trim(),
                description: description.trim(),
                route: location.pathname,
                browserMeta: getBrowserMetaSnapshot(),
            })
            // Offline-queued path returns {_queued: true, ...} — no real ticket id.
            if (ticket?._queued) {
                setCreatedTicketId(null)
                setState(STATES.DONE)
                setError(`Сохранили локально (${ticket.queueLength} в очереди). Отправим автоматически когда появится сеть.`)
                return
            }
            if (withScreenshot && screenshotBlob) {
                await uploadScreenshot(ticket.id, screenshotBlob, {
                    maskedCount,
                })
            }
            if (videoBlob) {
                await uploadAttachmentTyped(ticket.id, videoBlob, 'video', {
                    filename: 'recording.webm', mime: videoMime,
                })
            }
            if (audioBlob) {
                await uploadAttachmentTyped(ticket.id, audioBlob, 'audio', {
                    filename: 'voice.webm', mime: audioMime,
                })
            }
            setCreatedTicketId(ticket.id)
            setState(STATES.DONE)
        } catch (e) {
            setError(`Не удалось отправить обращение: ${e?.message || 'unknown'}`)
            setState(STATES.ERROR)
        }
    }

    // Auto-close after success via deep link.
    useEffect(() => {
        if (state !== STATES.DONE || !createdTicketId) return
        const t = setTimeout(() => {
            _closePanel()
        }, 8000)
        return () => clearTimeout(t)
    }, [state, createdTicketId])  // eslint-disable-line react-hooks/exhaustive-deps

    if (!user || isHiddenRoute) return null
    if (typeof document === 'undefined') return null

    const fab = open ? null : (
        <button
            type="button"
            aria-label="Открыть поддержку"
            onClick={_openPanel}
            className="conversation-fab"
            title="Поддержка"
        >
            <LifeBuoy size={22} />
        </button>
    )

    const panel = (
        <Conversation
            open={open}
            onClose={_closePanel}
            variant="widget"
            ariaLabel="Поддержка"
        >
            <div
                ref={widgetRef}
                onDragEnter={_onDragEnter}
                onDragOver={_onDragOver}
                onDragLeave={_onDragLeave}
                onDrop={_onDrop}
                style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, position: 'relative' }}
            >
            {dragActive && mode === 'chat' && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        inset: 6,
                        zIndex: 20,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 8,
                        borderRadius: 14,
                        border: '2px dashed var(--color-accent)',
                        background: 'color-mix(in srgb, var(--color-accent) 12%, var(--color-bg))',
                        color: 'var(--color-accent)',
                        fontSize: 14,
                        fontWeight: 600,
                    }}
                >
                    <Paperclip size={18} /> Отпустите, чтобы приложить
                </div>
            )}
            <ConversationHeader
                icon={LifeBuoy}
                title="Поддержка"
                subtitle={mode === 'chat' ? 'Спросите — отвечу или передам нужному' : 'Обращение с вложениями'}
                onClose={_closePanel}
            />

            {mode === 'chat' && (
                <>
                    <MessageStream
                        messages={messages}
                        sending={chatSending}
                        emptySlot={
                            <EmptyState
                                heroIcon={LifeBuoy}
                                heading="Чем помочь?"
                                intro={
                                    <>
                                        <p className="conversation__empty-intro">
                                            Опишите проблему словами или приложите скриншот — отвечу сразу или передам нужному человеку.
                                        </p>
                                        <SupportHowItWorks />
                                    </>
                                }
                                suggestions={CHAT_SUGGESTIONS}
                                onPick={_sendChat}
                                disabled={chatSending}
                                suggestLabel="Частые вопросы"
                            />
                        }
                    />

                    {chatMedia.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 6px' }}>
                            {chatMedia.map((m, i) => (
                                <span
                                    key={`${m.name}-${i}`}
                                    className="row-center-6 text-xs"
                                    style={{
                                        padding: '3px 8px',
                                        borderRadius: 14,
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-soft)',
                                        maxWidth: 180,
                                    }}
                                    title={m.name}
                                >
                                    <Paperclip size={11} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                    <button
                                        type="button"
                                        aria-label="Убрать вложение"
                                        onClick={() => setChatMedia((prev) => prev.filter((_, j) => j !== i))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {uploadingVideo && (
                        <div style={{ padding: '0 14px 6px' }}>
                            <div className="row-center-6 text-xs text-muted" style={{ marginBottom: 4 }}>
                                <Video size={12} style={{ flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Загрузка видео «{uploadingVideo.name}» — {uploadingVideo.pct}%
                                </span>
                            </div>
                            <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: `${uploadingVideo.pct}%`,
                                        height: '100%',
                                        background: 'var(--color-accent)',
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {pasteNote && (
                        <div className="text-xs text-muted" style={{ padding: '0 14px 4px', display: 'flex', gap: 6 }}>
                            <ShieldAlert size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span>{pasteNote}</span>
                        </div>
                    )}
                    {!online && (
                        <div className="row-center-6 text-xs" style={{ padding: '0 14px 4px', color: 'var(--color-warning, var(--color-warning))' }}>
                            <WifiOff size={12} /> Нет связи — сообщение сохранится, отправьте ещё раз, когда сеть появится.
                        </div>
                    )}
                    {error && (
                        <div className="row-center-6 text-xs" style={{ padding: '0 14px 4px', color: 'var(--color-danger)' }}>
                            <ShieldAlert size={12} /> {error}
                        </div>
                    )}

                    <SupportMediaToolbar
                        disabled={chatSending}
                        onScreenshot={_captureScreenshotToChat}
                        onRecordVideo={() => setChatRecording('video')}
                        onRecordAudio={() => setChatRecording('audio')}
                        onPickFiles={_onPickFiles}
                    />

                    <Composer
                        value={chatInput}
                        onChange={setChatInput}
                        onSubmit={_sendChat}
                        submitOnEnter
                        placeholder="Опишите проблему… или вставьте скриншот (Ctrl+V)"
                        disabled={chatSending}
                        autoFocus
                        showVoice
                    />

                    {/* U-1: persistent privacy disclaimer — no blocking consent click. */}
                    <SupportPrivacyHint expanded={showCollect} onToggle={() => setShowCollect((v) => !v)} />

                    {/* U-3: video/voice recorder inline — overlays the chat, no form-mode switch. */}
                    {chatRecording && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 30,
                                background: 'var(--color-bg)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                padding: 16,
                                overflowY: 'auto',
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn--secondary btn--sm"
                                onClick={() => setChatRecording(null)}
                                style={{ alignSelf: 'flex-start' }}
                            >
                                ← Назад к чату
                            </button>
                            {chatRecording === 'video' ? (
                                <VideoRecorder onCancel={() => setChatRecording(null)} onComplete={_onRecordingComplete} />
                            ) : (
                                <AudioRecorder onCancel={() => setChatRecording(null)} onComplete={_onRecordingComplete} />
                            )}
                        </div>
                    )}
                </>
            )}

            {mode === 'form' && (
            <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
                <button
                    type="button"
                    onClick={() => { setMode('chat'); setState(STATES.IDLE) }}
                    className="btn btn--secondary btn--sm"
                    style={{ marginBottom: 12 }}
                >
                    ← Вернуться к чату
                </button>
                {!online && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        marginBottom: 12,
                        background: 'var(--color-warning-bg)',
                        color: 'var(--color-warning)',
                        border: '1px solid color-mix(in srgb, var(--color-warning) 35%, transparent)',
                        borderRadius: 10,
                        fontSize: 12,
                    }}>
                        <WifiOff size={14} />
                        Нет связи. Обращение сохранится локально и отправится автоматически, когда сеть появится.
                    </div>
                )}
                {outboxDrained > 0 && online && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        marginBottom: 12,
                        background: 'var(--color-success-bg)',
                        color: 'var(--color-success)',
                        border: '1px solid color-mix(in srgb, var(--color-success) 35%, transparent)',
                        borderRadius: 10,
                        fontSize: 12,
                    }}>
                        <CheckCircle2 size={14} />
                        Отправлено {outboxDrained} {outboxDrained === 1 ? 'обращение' : 'обращений'} из оффлайн-очереди.
                    </div>
                )}

                {(state === STATES.IDLE || state === STATES.ERROR) && (
                    <form onSubmit={_submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Тема *</span>
                            <input
                                type="text"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                maxLength={256}
                                placeholder="Например: не открывается карточка кандидата"
                                style={{
                                    padding: 8,
                                    borderRadius: 8,
                                    border: '1px solid var(--color-border)',
                                    background: 'var(--color-bg)',
                                    color: 'var(--color-text)',
                                    fontSize: 13,
                                }}
                            />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                Что произошло (опционально)
                            </span>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={40_000}
                                rows={4}
                                placeholder="Что вы делали, что ожидали, что получилось"
                                style={{
                                    padding: 8,
                                    borderRadius: 8,
                                    border: '1px solid var(--color-border)',
                                    background: 'var(--color-bg)',
                                    color: 'var(--color-text)',
                                    fontFamily: 'inherit',
                                    fontSize: 13,
                                    resize: 'vertical',
                                }}
                            />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input
                                type="checkbox"
                                checked={withScreenshot}
                                onChange={(e) => {
                                    setWithScreenshot(e.target.checked)
                                    if (e.target.checked && !screenshotBlob) _capture()
                                    if (!e.target.checked) {
                                        setScreenshotBlob(null)
                                        setScreenshotDataUrl(null)
                                    }
                                }}
                            />
                            <Camera size={14} /> Приложить скриншот текущего экрана
                        </label>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="btn btn--secondary btn--sm"
                                onClick={() => setState(STATES.RECORDING_VIDEO)}
                                style={{ fontSize: 12, flex: 1 }}
                            >
                                <Video size={14} style={{ marginRight: 4 }} />
                                {videoBlob ? 'Видео ✓' : 'Видео'}
                            </button>
                            <button
                                type="button"
                                className="btn btn--secondary btn--sm"
                                onClick={() => setState(STATES.RECORDING_AUDIO)}
                                style={{ fontSize: 12, flex: 1 }}
                            >
                                <Mic size={14} style={{ marginRight: 4 }} />
                                {audioBlob ? 'Голос ✓' : 'Голос'}
                            </button>
                        </div>

                        {error && (
                            <div style={{
                                padding: 8,
                                background: 'var(--color-danger-bg)',
                                color: 'var(--color-danger)',
                                borderRadius: 6,
                                fontSize: 12,
                            }}>
                                {error}
                            </div>
                        )}

                        <button type="submit" className="btn btn--primary btn--sm" disabled={!summary.trim()}>
                            <Send size={14} style={{ marginRight: 6 }} />
                            Отправить обращение
                        </button>
                    </form>
                )}

                {state === STATES.CAPTURING && (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Готовим скриншот…</p>
                )}

                {state === STATES.RECORDING_VIDEO && (
                    <VideoRecorder
                        onCancel={() => setState(STATES.IDLE)}
                        onComplete={({ blob, mime }) => {
                            setVideoBlob(blob)
                            setVideoMime(mime)
                            setState(STATES.PREVIEWING_VIDEO)
                        }}
                    />
                )}

                {state === STATES.PREVIEWING_VIDEO && videoBlob && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <video controls src={URL.createObjectURL(videoBlob)} style={{ width: '100%', borderRadius: 8 }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
                                setVideoBlob(null); setVideoMime(null); setState(STATES.RECORDING_VIDEO)
                            }}>Перезаписать</button>
                            <button type="button" className="btn btn--primary btn--sm" onClick={() => setState(STATES.IDLE)} style={{ marginLeft: 'auto' }}>
                                К обращению
                            </button>
                        </div>
                    </div>
                )}

                {state === STATES.RECORDING_AUDIO && (
                    <AudioRecorder
                        onCancel={() => setState(STATES.IDLE)}
                        onComplete={({ blob, mime }) => {
                            setAudioBlob(blob)
                            setAudioMime(mime)
                            setState(STATES.PREVIEWING_AUDIO)
                        }}
                    />
                )}

                {state === STATES.PREVIEWING_AUDIO && audioBlob && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <audio controls src={URL.createObjectURL(audioBlob)} style={{ width: '100%' }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
                                setAudioBlob(null); setAudioMime(null); setState(STATES.RECORDING_AUDIO)
                            }}>Перезаписать</button>
                            <button type="button" className="btn btn--primary btn--sm" onClick={() => setState(STATES.IDLE)} style={{ marginLeft: 'auto' }}>
                                К обращению
                            </button>
                        </div>
                    </div>
                )}

                {state === STATES.PREVIEWING && screenshotDataUrl && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            Авто-маска применена к {maskedCount} элементам. При необходимости закрасьте кистью то,
                            что должно остаться приватным, и отправьте.
                        </div>
                        <BlurBrush
                            dataUrl={screenshotDataUrl}
                            onChange={({ blob, dataUrl }) => {
                                setScreenshotBlob(blob)
                                setScreenshotDataUrl(dataUrl)
                            }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setState(STATES.IDLE)}>
                                Назад
                            </button>
                            <button type="button" className="btn btn--primary btn--sm" onClick={_submit} style={{ marginLeft: 'auto' }}>
                                <Send size={14} style={{ marginRight: 6 }} />
                                Отправить
                            </button>
                        </div>
                    </div>
                )}

                {state === STATES.SUBMITTING && (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Отправляем…</p>
                )}

                {state === STATES.DONE && createdTicketId && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <p style={{ margin: 0, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Check size={14} style={{ color: 'var(--color-accent)' }} />
                            Обращение зарегистрировано. Мы вернёмся с ответом в этот же тикет.
                        </p>
                        <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            onClick={() => {
                                _closePanel()
                                navigate(`/support/${createdTicketId}`)
                            }}
                        >
                            Открыть тикет
                        </button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={_closePanel}>
                            Закрыть
                        </button>
                    </div>
                )}
            </div>
            )}
            </div>
        </Conversation>
    )

    // PR-8: FAB removed — widget opens only via `onboardix:support:open` /
    // `onboardix:support:prefill` events. AIChatWidget owns the single floating
    // button so the support icon doesn't overlap the AI assistant one.
    // The `fab` variable is intentionally not rendered; kept for future toggle.
    void fab
    return createPortal(<>{panel}</>, document.body)
}
